import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
  HostListener, Input, NgZone, OnChanges, OnDestroy, QueryList, ViewChild, ViewChildren,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { WindowRef } from '@spartacus/core';
import { HamburgerMenuService, ICON_TYPE, NavigationNode } from '@spartacus/storefront';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GenGoogleAnalyticsService } from '../../../services/google-analytics.service';
import { primaryCategoryCount } from './category-menu.model';

@Component({
  selector: 'gen-category-menu',
  templateUrl: './category-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GenCategoryMenuComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() node: NavigationNode | null;
  @Input() navAriaLabel: string;
  @ViewChild('bar') bar: ElementRef<HTMLElement>;
  @ViewChild('moreMeasure') moreMeasure: ElementRef<HTMLElement>;
  @ViewChildren('measure') measures: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('backButton') backButton: ElementRef<HTMLButtonElement>;
  readonly iconType = ICON_TYPE;
  categories: NavigationNode[] = [];
  primary: NavigationNode[] = [];
  overflow: NavigationNode[] = [];
  active: NavigationNode | 'more' | null = null;
  columns: NavigationNode[][] = [];
  isMobile = false;
  private trigger?: HTMLElement;
  private observer?: ResizeObserver;
  private subscriptions = new Subscription();
  private frame?: number;
  private destroyed = false;

  constructor(
    private element: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private winRef: WindowRef,
    private hamburger: HamburgerMenuService,
    private analytics: GenGoogleAnalyticsService,
    router: Router,
  ) {
    this.subscriptions.add(router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.close()));
    this.subscriptions.add(this.hamburger.isExpanded.subscribe(expanded => {
      if (!expanded) this.close();
    }));
  }

  ngOnChanges(): void {
    this.categories = this.node?.children ?? [];
    this.partition(0);
    this.close();
    this.scheduleMeasure();
  }

  ngAfterViewInit(): void {
    if (!this.winRef.isBrowser()) return;
    this.subscriptions.add(this.measures.changes.subscribe(() => {
      this.observeMeasurements();
      this.scheduleMeasure();
    }));
    this.subscriptions.add(this.winRef.resize$.subscribe(() => this.scheduleMeasure()));
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.scheduleMeasure());
      this.observeMeasurements();
    }
    this.winRef.document.fonts?.ready.then(() => this.scheduleMeasure());
    this.scheduleMeasure();
  }

  private observeMeasurements(): void {
    this.observer?.disconnect();
    this.observer?.observe(this.element.nativeElement);
    this.measures.forEach(item => this.observer?.observe(item.nativeElement));
    this.observer?.observe(this.moreMeasure.nativeElement);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
    this.observer?.disconnect();
    if (this.frame !== undefined) this.winRef.nativeWindow?.cancelAnimationFrame(this.frame);
  }

  private scheduleMeasure(): void {
    const win = this.winRef.nativeWindow;
    if (!win || this.destroyed || this.frame !== undefined) return;
    this.frame = win.requestAnimationFrame(() => {
      this.frame = undefined;
      if (!this.bar) return;
      this.zone.run(() => {
        const mobile = win.innerWidth < 992;
        const widths = this.measures.toArray().map(item => item.nativeElement.getBoundingClientRect().width);
        // The vertical mobile menu shows four primary rows regardless of label width.
        const count = mobile ? Math.min(4, this.categories.length)
          : primaryCategoryCount(widths, this.element.nativeElement.clientWidth,
            this.moreMeasure.nativeElement.getBoundingClientRect().width);
        if (mobile !== this.isMobile || count !== this.primary.length) this.close();
        this.isMobile = mobile;
        this.partition(count);
        this.cdr.markForCheck();
      });
    });
  }

  private partition(count: number): void {
    this.primary = this.categories.slice(0, count);
    this.overflow = this.categories.slice(count);
  }

  open(category: NavigationNode | 'more', event: Event, toggle = false): void {
    if (toggle && this.active === category) {
      this.close();
      return;
    }
    this.trigger = event.currentTarget as HTMLElement;
    this.active = category;
    const nodes = category === 'more' ? this.overflow : category.children ?? [];
    this.columns = [];
    // Stack up to three consecutive leaf links, as in the Figma accessory menu.
    // Facets, branches and More's top-level categories retain their own columns.
    const isLeafLink = (node: NavigationNode) => !node.isFacetGroup && !!node.url && !node.children?.length;
    for (const node of nodes) {
      const previous = this.columns[this.columns.length - 1];
      if (category !== 'more' && isLeafLink(node) && previous?.length < 3 && previous.every(isLeafLink)) {
        previous.push(node);
      } else {
        this.columns.push([node]);
      }
    }
    if (this.isMobile) {
      this.cdr.detectChanges();
      this.backButton?.nativeElement.focus();
    } else if (event.type === 'keydown') {
      this.cdr.detectChanges();
      this.element.nativeElement.querySelector<HTMLElement>('.category-menu-panel a, .category-menu-panel button')?.focus();
    }
  }

  close(restoreFocus = false): void {
    this.active = null;
    this.columns = [];
    this.cdr.markForCheck();
    if (restoreFocus) {
      this.cdr.detectChanges();
      this.trigger?.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent): void {
    if (!this.element.nativeElement.contains(event.target as Node)) this.close();
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    if (event.relatedTarget && !this.element.nativeElement.contains(event.relatedTarget as Node)) this.close();
  }

  @HostListener('keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (this.active) {
      event.stopPropagation();
      this.close(true);
    }
  }

  select(node: NavigationNode): void {
    if (this.isEcosystem(node)) {
      this.analytics.sendGaEvent('entry_click', {
        entry_source: 'ecosystem_submenu', submenu_label: node.title,
      });
    }
    node.customClickHandler?.();
    this.close();
    if (this.isMobile) this.hamburger.toggle(true);
  }

  getNodeUrl(node: NavigationNode): string | string[] {
    if (this.isEcosystem(node) && typeof node.url === 'string') {
      const [path, hash] = node.url.split('#');
      return path + (path.includes('?') ? '&' : '?') + 'entry_source=ecosystem_submenu'
        + (hash ? '#' + hash : '');
    }
    return node.url;
  }

  private isEcosystem(node: NavigationNode): boolean {
    return node.parentNodeId === 'whole-home-ecosystem' || !!node.url?.includes('whole-home-ecosystem');
  }
}
